/**
 * Card processing-fee disclosure, shown before Stripe (COL-139 / COL-533).
 *
 * The tenant is charged subtotal + fee so CoLiviq receives the full net rent,
 * which means the amount that leaves their account is NOT the amount on the
 * rent row. They are told before they commit, not on the Stripe page.
 *
 * V2's Pay now went straight to checkout and skipped this. Neel confirmed on
 * 10 Oct that the notice stays, so it is shared rather than reimplemented:
 * the figures come from `formatCardFeeBreakdown`, the same helper V1 renders
 * and the mirror of the edge function that actually charges.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCardFeeBreakdown } from "@/lib/card-fees";

type Labels = {
  cardFeeTitle: string;
  cardFeeExplainer: string;
  cardFeeSubtotal: string;
  cardFeeProcessing: string;
  cardFeeTotal: string;
  continueToStripe: string;
  cancel: string;
};

type Props = {
  /** Net amount in cents, or null when the dialog is closed. */
  netCents: number | null;
  locale: string;
  labels: Labels;
  /** `true` = proceed to Stripe, `false` = cancelled. */
  onClose: (proceed: boolean) => void;
};

export default function CardFeeDialog({
  netCents,
  locale,
  labels,
  onClose,
}: Props) {
  const open = netCents != null && netCents > 0;
  const { subtotalCents, feeCents, totalCents } = formatCardFeeBreakdown(
    netCents ?? 0
  );
  const fmt = (c: number) =>
    `€${(c / 100).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing by backdrop or Esc is a cancellation, never a consent.
        if (!next) onClose(false);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{labels.cardFeeTitle}</DialogTitle>
          <DialogDescription>{labels.cardFeeExplainer}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {labels.cardFeeSubtotal}
            </span>
            <span className="font-medium text-foreground">
              {fmt(subtotalCents)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {labels.cardFeeProcessing}
            </span>
            <span className="font-medium text-foreground">{fmt(feeCents)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-2">
            <span className="font-semibold text-foreground">
              {labels.cardFeeTotal}
            </span>
            <span className="text-lg font-bold text-foreground">
              {fmt(totalCents)}
            </span>
          </div>
        </div>
        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onClose(false)}>
            {labels.cancel}
          </Button>
          <Button onClick={() => onClose(true)}>
            {labels.continueToStripe}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
